// Project Manager + Cost Dashboard views
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Rocket, Mic, MicOff, Loader2, Sparkles, CheckCircle2, Send,
  TrendingDown, Zap, DollarSign, Activity, ChevronRight
} from "lucide-react";
import { CHANNELS } from "./i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ======================================================
   Shared mic hook (duplicate-safe)
   ====================================================== */
function useRecorder(onBlob) {
  const [rec, setRec] = useState(false);
  const chunks = useRef([]); const mr = useRef(null);
  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunks.current = [];
      mr.current = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mr.current.ondataavailable = (e) => e.data.size > 0 && chunks.current.push(e.data);
      mr.current.onstop = () => {
        const blob = new Blob(chunks.current, { type: "audio/webm" });
        stream.getTracks().forEach((t) => t.stop()); onBlob(blob);
      };
      mr.current.start(); setRec(true);
    } catch (e) { alert("Mikrofon-Zugriff benötigt. " + e.message); }
  };
  const stop = () => { if (mr.current && mr.current.state !== "inactive") mr.current.stop(); setRec(false); };
  return { rec, start, stop };
}

async function transcribe(blob, lang) {
  const fd = new FormData();
  fd.append("file", blob, "voice.webm"); fd.append("language", lang);
  const r = await axios.post(`${API}/agents/stt`, fd);
  return r.data.text || "";
}

/* ======================================================
   PROJECT MANAGER AGENT — one voice-command → full deploy
   ====================================================== */
export function ProjectManager({ lang }) {
  const [sessionId] = useState(() => "proj-" + Math.random().toString(36).slice(2, 10));
  const [log, setLog] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [deployments, setDeployments] = useState([]);
  const [lastCreated, setLastCreated] = useState(null);

  const loadDepl = async () => {
    const r = await axios.get(`${API}/agents/project/deployments?limit=10`);
    setDeployments(r.data);
  };
  useEffect(() => { loadDepl(); }, []);

  const { rec, start, stop } = useRecorder(async (blob) => {
    setBusy(true);
    try {
      const text = await transcribe(blob, lang);
      if (text) await submit(text);
    } finally { setBusy(false); }
  });

  const submit = async (text) => {
    const t = text || input.trim();
    if (!t) return;
    setInput("");
    setLog((l) => [...l, { role: "admin", text: t }]);
    setBusy(true);
    try {
      const r = await axios.post(`${API}/agents/chat`, {
        agent: "project", session_id: sessionId, message: t, lang,
      });
      setLog((l) => [...l, { role: "agent", text: r.data.reply, action: r.data.project_action }]);
      // auto-execute if we got a CREATE_AND_PUBLISH action
      if (r.data.project_action?.action === "CREATE_AND_PUBLISH") {
        const er = await axios.post(`${API}/agents/project/execute`, r.data.project_action);
        setLastCreated(er.data);
        setLog((l) => [...l, { role: "system",
          text: `✓ ${er.data.property.name} angelegt (${er.data.property.easybooking_id}) und auf ${er.data.channel_log.length} Portale veröffentlicht.`,
          deployment: er.data }]);
        await loadDepl();
      }
    } catch (e) {
      setLog((l) => [...l, { role: "system", text: "Fehler: " + (e.response?.data?.detail || e.message) }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="project-manager-view">
      <div>
        <h2 className="font-heading font-bold text-2xl flex items-center gap-2"><Rocket size={22} className="text-eb-primary" /> Projekt-Manager-Agent</h2>
        <p className="text-eb-muted text-sm">Ein Sprachbefehl → Objekt angelegt → auf allen Portalen veröffentlicht. „Leg ein neues Chalet an …"</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-4">
        <div className="card !p-0 h-[58vh] flex flex-col">
          <div className="flex-1 overflow-y-auto p-5 space-y-3">
            {log.length === 0 && (
              <div className="text-eb-muted text-sm">
                <div className="mb-2"><strong className="text-eb-fg">Beispielbefehl:</strong></div>
                <div className="eb-surface rounded p-3 italic">
                  „Leg bitte ein neues Chalet an namens Chalet Sonnblick in Mayrhofen Tirol, 4 Schlafzimmer, 3 Bäder, 8 Personen, Grundpreis 520 Euro, Endreinigung 90 Euro, mit Sauna, Kamin, WLAN und Skiraum. Veröffentliche auf allen Portalen."
                </div>
              </div>
            )}
            {log.map((e, i) => (
              <div key={i}>
                {e.role === "system" ? (
                  <div className="flex items-center gap-2 text-emerald-400 text-sm font-mono bg-emerald-500/10 border border-emerald-500/30 rounded px-3 py-2" data-testid={`project-system-${i}`}>
                    <CheckCircle2 size={14} /> {e.text}
                  </div>
                ) : (
                  <>
                    <div className="overline mb-1">{e.role === "admin" ? "ADMIN" : "AGENT"}</div>
                    <div className={`rounded px-3 py-2 text-sm whitespace-pre-wrap ${
                      e.role === "admin" ? "bg-eb-primary/20 text-eb-primary font-mono" : "eb-surface border border-white/5"
                    }`} data-testid={`project-log-${i}`}>
                      {e.text}
                    </div>
                    {e.action && (
                      <div className="mt-2 flex gap-2 items-center">
                        <span className="channel-pill" style={{ background: "#D4AF3722", color: "#D4AF37", border: "1px solid #D4AF3744" }}>
                          <Sparkles size={10} className="inline mr-1" /> {e.action.action}
                        </span>
                        <span className="text-xs text-eb-muted">{e.action.property?.name}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
          <div className="border-t border-white/5 p-3 flex gap-2">
            <button
              onClick={rec ? stop : start} disabled={busy}
              className="btn !px-3"
              style={{ background: rec ? "#F43F5E" : "transparent", color: rec ? "#fff" : "inherit" }}
              data-testid="project-mic-button"
            >
              {rec ? <MicOff size={14} /> : <Mic size={14} />}
            </button>
            <input value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder="Sprich oder tippe: „Leg ein neues Objekt an …"
              disabled={busy} data-testid="project-input" />
            <button className="btn btn-primary" onClick={() => submit()} disabled={busy} data-testid="project-send-button">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </div>
        </div>

        <div className="card">
          <div className="overline mb-3">Letzte Deployments</div>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {deployments.length === 0 && <div className="text-eb-muted text-sm">—</div>}
            {deployments.map((d) => (
              <div key={d.id} className="eb-surface border border-white/5 rounded p-3" data-testid={`project-deploy-${d.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-sm">{d.property_name}</span>
                  <span className="font-mono text-[10px] text-eb-muted">{(d.created_at || "").slice(0, 10)}</span>
                </div>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {(d.channels || []).map((c, i) => (
                    <span key={c.channel} className="channel-pill" style={{
                      background: CHANNELS[c.channel]?.bg || "#374151",
                      color: CHANNELS[c.channel]?.fg || "#fff", fontSize: 9
                    }}>
                      {c.channel.slice(0, 6)}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {lastCreated && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="card border-emerald-500/40" style={{ borderColor: "rgba(16,185,129,0.4)" }}>
          <div className="overline text-emerald-400 mb-2">✓ Live auf allen Portalen</div>
          <div className="grid md:grid-cols-5 gap-2">
            {lastCreated.channel_log.map((c, i) => (
              <div key={c.channel} className="eb-surface border border-white/5 rounded p-2" data-testid={`project-publish-${c.channel}`}>
                <div className="overline mb-0.5">{c.channel}</div>
                <div className="font-mono text-[10px] text-eb-primary">{c.listing_id}</div>
                <div className="text-[10px] text-emerald-400">{c.status}</div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}

/* ======================================================
   COST DASHBOARD — tracks LLM/STT/TTS usage + cost
   ====================================================== */
export function CostDashboard() {
  const [sum, setSum] = useState(null);
  const [recent, setRecent] = useState([]);
  const [providers, setProviders] = useState({});
  const [routing, setRouting] = useState({});
  const [defaults, setDefaults] = useState({});
  const [saved, setSaved] = useState(false);
  const AGENTS = ["chat", "voice", "booking", "admin", "project"];

  const load = async () => {
    const [s, r, p, rt] = await Promise.all([
      axios.get(`${API}/agents/cost/summary`),
      axios.get(`${API}/agents/cost/recent?limit=30`),
      axios.get(`${API}/agents/providers`),
      axios.get(`${API}/agents/routing`),
    ]);
    setSum(s.data); setRecent(r.data); setProviders(p.data);
    setRouting(rt.data.routing || {}); setDefaults(rt.data.defaults || {});
  };
  useEffect(() => { load(); }, []);

  const saveRouting = async () => {
    await axios.put(`${API}/agents/routing`, routing);
    setSaved(true); setTimeout(() => setSaved(false), 2000);
  };
  const updateRoute = (agent, patch) =>
    setRouting((r) => ({ ...r, [agent]: { ...(r[agent] || defaults[agent] || {}), ...patch } }));

  if (!sum) return <div className="text-eb-muted text-sm">Lade…</div>;

  const Kpi = ({ label, value, suffix, accent, test }) => (
    <div className="card relative overflow-hidden" data-testid={test}>
      <div className="overline mb-2">{label}</div>
      <div className="flex items-baseline gap-1">
        <span className="font-heading text-2xl font-extrabold" style={{ color: accent }}>{value}</span>
        {suffix && <span className="text-eb-muted text-xs font-mono">{suffix}</span>}
      </div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="cost-dashboard-view">
      <div>
        <h2 className="font-heading font-bold text-2xl flex items-center gap-2"><Activity size={22} className="text-eb-primary" /> KI-Kosten-Management</h2>
        <p className="text-eb-muted text-sm">
          Token-Tracking · Pro-Agent-Routing · Kostenlose Alternativen via Groq / Gemini Flash / DeutschlandGPT / Ollama
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Heute" value={`$ ${sum.today.cost_usd.toFixed(4)}`} suffix={`· ${sum.today.calls} Calls`} accent="var(--eb-primary)" test="cost-today" />
        <Kpi label="7 Tage" value={`$ ${sum.week.cost_usd.toFixed(4)}`} suffix={`· ${sum.week.calls} Calls`} accent="var(--eb-sky)" test="cost-week" />
        <Kpi label="30 Tage" value={`$ ${sum.month.cost_usd.toFixed(4)}`} suffix={`· ${sum.month.calls} Calls`} accent="var(--eb-emerald)" test="cost-month" />
        <Kpi label="Tokens heute" value={`${(sum.today.input_tokens + sum.today.output_tokens).toLocaleString()}`} suffix="in+out" accent="var(--eb-amber)" test="cost-tokens" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card">
          <div className="overline mb-3">Kosten pro Agent (30 Tage)</div>
          {sum.by_agent.length === 0 && <div className="text-eb-muted text-sm">Noch keine Daten.</div>}
          {sum.by_agent.map((a) => {
            const max = Math.max(...sum.by_agent.map((x) => x.cost_usd), 0.0001);
            return (
              <div key={a.agent} className="mb-2.5" data-testid={`cost-agent-${a.agent}`}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-semibold">{a.agent}</span>
                  <span className="font-mono">$ {a.cost_usd.toFixed(4)} · {a.calls}×</span>
                </div>
                <div className="h-1.5 bg-white/5 rounded">
                  <div className="h-full rounded" style={{ width: `${(a.cost_usd / max) * 100}%`, background: "linear-gradient(90deg,#D4AF37,#F43F5E)" }} />
                </div>
              </div>
            );
          })}
        </div>

        <div className="card">
          <div className="overline mb-3">Pro Provider</div>
          {sum.by_provider.map((p, i) => (
            <div key={`${p.provider}-${p.model}`} className="flex items-center justify-between py-1.5 border-b border-white/5 text-xs" data-testid={`cost-provider-${i}`}>
              <div>
                <div className="font-semibold">{p.provider}</div>
                <div className="font-mono text-[10px] text-eb-muted">{p.model}</div>
              </div>
              <div className="text-right">
                <div className="font-mono">$ {p.cost_usd.toFixed(4)}</div>
                <div className="text-[10px] text-eb-muted">{p.calls} Calls</div>
              </div>
            </div>
          ))}
          {sum.by_provider.length === 0 && <div className="text-eb-muted text-sm">—</div>}
        </div>
      </div>

      {/* Routing editor */}
      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-heading font-bold text-base flex items-center gap-2"><Zap size={16} className="text-eb-amber" /> Agent → Provider Routing</div>
            <div className="text-xs text-eb-muted">Wähle pro Agent einen Anbieter. Markiere kostenlose Anbieter grün (Groq, Gemini-Flash, Ollama).</div>
          </div>
          <button className="btn btn-primary" onClick={saveRouting} data-testid="cost-save-routing">
            {saved ? <><CheckCircle2 size={13} /> gespeichert</> : "Routing speichern"}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {AGENTS.map((a) => {
            const cur = routing[a] || defaults[a] || {};
            return (
              <div key={a} className="eb-surface border border-white/5 rounded p-3" data-testid={`route-${a}`}>
                <div className="overline mb-2">{a}</div>
                <select value={cur.provider || "anthropic"}
                  onChange={(e) => updateRoute(a, { provider: e.target.value })}>
                  <option value="anthropic">anthropic (Claude · Premium)</option>
                  <option value="openai">openai (GPT)</option>
                  <option value="gemini">gemini</option>
                  <option value="groq">🟢 groq (KOSTENLOS)</option>
                  <option value="gemini_free">🟢 gemini-free (KOSTENLOS)</option>
                  <option value="ollama">🟢 ollama (lokal)</option>
                  <option value="deutschlandgpt">deutschlandgpt (DE/EU)</option>
                  <option value="deepseek">deepseek (günstig)</option>
                </select>
                <input className="mt-1.5"
                  value={cur.model || ""} placeholder="Modell-Name"
                  onChange={(e) => updateRoute(a, { model: e.target.value })} />
              </div>
            );
          })}
        </div>
      </div>

      {/* Known providers help panel */}
      <div className="card">
        <div className="overline mb-3">Kostenlose & günstige Anbieter — Setup-Anleitung</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(providers).filter(([k]) => k !== "emergent").map(([k, v]) => (
            <div key={k} className="eb-surface border border-white/5 rounded p-3" data-testid={`provider-info-${k}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="font-semibold text-sm">{v.label}</div>
                {v.free && <span className="channel-pill" style={{ background: "#10B98122", color: "#10B981" }}>FREE</span>}
              </div>
              {v.base_url && (
                <div className="font-mono text-[10px] text-eb-primary break-all mb-1">{v.base_url}</div>
              )}
              {v.models && (
                <div className="text-[11px] text-eb-muted mb-1.5">
                  {v.models.slice(0, 3).join(" · ")}{v.models.length > 3 ? "…" : ""}
                </div>
              )}
              {v.signup && (
                <a href={v.signup} target="_blank" rel="noreferrer"
                   className="text-[11px] text-eb-primary hover:underline inline-flex items-center gap-0.5">
                  API-Key holen <ChevronRight size={11} />
                </a>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Recent calls */}
      <div className="card !p-0 overflow-hidden">
        <div className="overline px-4 py-3 border-b border-white/5">Letzte Calls (30)</div>
        <table className="w-full text-xs">
          <thead>
            <tr className="eb-surface">
              <th className="text-left px-3 py-2">Zeit</th>
              <th className="text-left px-3 py-2">Agent</th>
              <th className="text-left px-3 py-2">Provider / Modell</th>
              <th className="text-right px-3 py-2">In</th>
              <th className="text-right px-3 py-2">Out</th>
              <th className="text-right px-3 py-2">ms</th>
              <th className="text-right px-3 py-2">USD</th>
            </tr>
          </thead>
          <tbody>
            {recent.map((r, i) => (
              <tr key={i} className="border-t border-white/5 font-mono" data-testid={`cost-call-${i}`}>
                <td className="px-3 py-1.5 text-eb-muted">{r.at.slice(11, 19)}</td>
                <td className="px-3 py-1.5">{r.agent}</td>
                <td className="px-3 py-1.5">{r.provider} / {r.model}</td>
                <td className="px-3 py-1.5 text-right">{r.input_tokens}</td>
                <td className="px-3 py-1.5 text-right">{r.output_tokens}</td>
                <td className="px-3 py-1.5 text-right">{r.duration_ms}</td>
                <td className="px-3 py-1.5 text-right text-eb-primary">${r.cost_usd.toFixed(5)}</td>
              </tr>
            ))}
            {recent.length === 0 && <tr><td colSpan={7} className="text-eb-muted text-center p-6">Noch keine Calls</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
